/**
 * Public per-user booking page — /book/:slug
 *
 * Unauthenticated by design: anyone holding the link can book. It renders the
 * same MeetingBookingTab used by the Help Desk, but scoped by `ownerSlug` so
 * the calendar shows only this owner's availability and the booking is
 * assigned to them (instead of the shared random-owner pool).
 *
 * The slug is resolved server-side to a display name; an unknown or revoked
 * slug renders a dead-link message rather than an empty calendar, so a stale
 * link is unambiguous to whoever received it.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Center,
  Container,
  Heading,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import MeetingBookingTab from "../components/MeetingBookingTab";
import { getBackendURL } from "../components/env";

const BookMeetingPage = () => {
  const { slug } = useParams();
  const [ownerName, setOwnerName] = useState("");
  const [state, setState] = useState("loading"); // loading | ready | notfound | error

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${getBackendURL()}/api/meeting-bookings/owner/?slug=${encodeURIComponent(slug)}`,
        );
        if (cancelled) return;
        if (res.status === 404) {
          setState("notfound");
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const j = await res.json();
        if (cancelled) return;
        setOwnerName(j.owner_name || "");
        setState("ready");
      } catch (_) {
        if (!cancelled) setState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (state === "loading") {
    return (
      <Center h="60vh">
        <Spinner size="lg" />
      </Center>
    );
  }

  if (state === "notfound" || state === "error") {
    return (
      <Center h="60vh" px={4}>
        <VStack spacing={3} textAlign="center">
          <Heading size="md">
            {state === "notfound" ? "This booking link is no longer active" : "Something went wrong"}
          </Heading>
          <Text color="gray.600">
            {state === "notfound"
              ? "The link may have been regenerated. Ask for an up-to-date one."
              : "Please try again in a moment."}
          </Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Container maxW="3xl" py={8}>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Heading size="lg">
            {ownerName ? `Book a meeting with ${ownerName}` : "Book a meeting"}
          </Heading>
          <Text color="gray.600" mt={1}>
            Pick a time that works for you. Times shown are Eastern Time (ET).
          </Text>
        </Box>
        <MeetingBookingTab
          ownerSlug={slug}
          isSignedIn={false}
          authChecked
        />
      </VStack>
    </Container>
  );
};

export default BookMeetingPage;
